import { useState } from "react";
import { toast } from "react-toastify";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import Pagination from "../../components/Pagination";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { StatusBadge } from "../../features/web3/forex/components/StatusBadge";
import { FormDialog, inputCls, labelCls } from "../../features/web3/forex/components/FormDialog";
import { usePermissions } from "../../hooks/usePermissions";
import { formatDateTime } from "../../lib/format";
import { useDeleteFaq, useFaqCategories, useFaqList, useSaveFaq, useToggleFaq } from "./api";

const PAGE_SIZES = [10, 20, 50, 100];

const DEFAULT_FILTERS = { page: 1, pageSize: 20, search: "", category: "", isActive: "" };

const EMPTY_FORM = { question: "", answer: "", category: "", order: 0, isActive: true };

const COLUMNS = [
  { label: "Order", align: "right" },
  { label: "Question" },
  { label: "Answer" },
  { label: "Category" },
  { label: "Status" },
  { label: "Created by" },
  { label: "Updated by" },
  { label: "Created" },
  { label: "Updated" },
  { label: "", align: "right", sticky: true },
];

export default function Faqs() {
  const { can } = usePermissions();
  const canCreate = can("POST:/api/admin/faqs");
  const canEdit = can("POST:/api/admin/faqs/:id/edit");
  const canDelete = can("POST:/api/admin/faqs/:id/delete");
  const canToggle = can("POST:/api/admin/faqs/:id/toggle");

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const list = useFaqList(filters);
  const categories = useFaqCategories();
  const save = useSaveFaq();
  const remove = useDeleteFaq();
  const toggle = useToggleFaq();

  // editing: null = closed, {} = new, row = edit. deleting: the row to confirm.
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleting, setDeleting] = useState(null);

  const rows = list.data?.data;

  const setField = (e) => setDraft((d) => ({ ...d, [e.target.name]: e.target.value }));
  const applyFilters = () => setFilters({ ...draft, page: 1 });
  const clearFilters = () => {
    setDraft(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
  };
  const goToPage = (page) => {
    setDraft((d) => ({ ...d, page }));
    setFilters((f) => ({ ...f, page }));
  };

  const openForm = (row) => {
    setForm(
      row
        ? { question: row.question, answer: row.answer, category: row.category, order: row.order, isActive: row.isActive }
        : EMPTY_FORM,
    );
    setEditing(row ?? {});
  };

  const runSave = async () => {
    try {
      await save.mutateAsync({ id: editing._id, ...form, order: Number(form.order) || 0 });
      toast.success(editing._id ? "FAQ updated" : "FAQ created");
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed");
    }
  };

  const runDelete = async () => {
    try {
      await remove.mutateAsync(deleting._id);
      toast.success("FAQ deleted");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
    setDeleting(null);
  };

  const runToggle = async (row) => {
    try {
      await toggle.mutateAsync(row._id);
      toast.success(row.isActive ? "FAQ hidden" : "FAQ shown");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Status change failed");
    }
  };

  return (
    <QueueLayout
      eyebrow="Content"
      title="FAQs"
      description="Questions and answers shown on the website. Only active ones are public."
      isFetching={list.isFetching}
      onRefresh={() => {
        list.refetch();
        categories.refetch();
      }}
    >
      <div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className={labelCls}>Search</label>
            <input
              name="search"
              value={draft.search}
              onChange={setField}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="Question, answer or category"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <select name="category" value={draft.category} onChange={setField} className={inputCls}>
              <option value="">All categories</option>
              {categories.data?.map((c) => (
                <option key={c.category} value={c.category}>
                  {c.category} ({c.activeCount}/{c.count} active)
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select name="isActive" value={draft.isActive} onChange={setField} className={inputCls}>
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select name="pageSize" value={draft.pageSize} onChange={setField} className={inputCls}>
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={applyFilters}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green transition-colors hover:bg-ginox-green/20"
          >
            Apply Filters
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
          >
            Clear Filters
          </button>
          {canCreate ? (
            <Button size="sm" className="ml-auto" onClick={() => openForm(null)}>
              <Plus className="h-3.5 w-3.5" />
              Add FAQ
            </Button>
          ) : null}
        </div>
      </div>

      {list.error ? (
        <ErrorBanner error={list.error} onRetry={() => list.refetch()} />
      ) : (
        <>
          <QueueTable
            columns={COLUMNS}
            isLoading={list.isLoading}
            rows={rows}
            emptyText="No FAQs found."
            renderRow={(row) => (
              <tr key={row._id} className="transition-colors hover:bg-white/[0.02]">
                <Td align="right" className="font-mono">{row.order}</Td>
                <Td className="min-w-[240px] max-w-[360px] whitespace-normal font-medium">{row.question}</Td>
                <Td className="max-w-[320px] text-muted-foreground">
                  <p className="line-clamp-2 whitespace-pre-line">{row.answer}</p>
                </Td>
                <Td className="text-muted-foreground">{row.category}</Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={row.isActive}
                      disabled={!canToggle || toggle.isPending}
                      onCheckedChange={() => runToggle(row)}
                    />
                    <StatusBadge status={row.isActive ? "ACTIVE" : "INACTIVE"} />
                  </div>
                </Td>
                <Td className="text-muted-foreground">{row.createdBy || "—"}</Td>
                <Td className="text-muted-foreground">{row.updatedBy || "—"}</Td>
                <Td className="whitespace-nowrap font-mono text-muted-foreground">{formatDateTime(row.createdAt)}</Td>
                <Td className="whitespace-nowrap font-mono text-muted-foreground">{formatDateTime(row.updatedAt)}</Td>
                <Td align="right" sticky>
                  <div className="flex justify-end gap-2">
                    {canEdit ? (
                      <Button size="sm" variant="outline" onClick={() => openForm(row)}>
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button size="sm" variant="outline" onClick={() => setDeleting(row)}>
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </Button>
                    ) : null}
                  </div>
                </Td>
              </tr>
            )}
          />

          <Pagination
            currentPage={Number(list.data?.currentPage) || filters.page}
            totalPages={list.data?.totalPages ?? 1}
            totalRows={list.data?.totalRows ?? 0}
            pageSize={Number(filters.pageSize)}
            onPageChange={goToPage}
            label="FAQs"
          />
        </>
      )}

      <FormDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing?._id ? "Edit FAQ" : "Add FAQ"}
        onSubmit={runSave}
        submitLabel={editing?._id ? "Save" : "Create"}
        submitDisabled={!form.question.trim() || !form.answer.trim()}
        loading={save.isPending}
      >
        <div>
          <label className={labelCls}>Question</label>
          <input
            value={form.question}
            maxLength={500}
            onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Answer</label>
          <Textarea
            rows={6}
            value={form.answer}
            onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Category</label>
            {/* Pick an existing category or type a new one. Empty saves as General. */}
            <input
              list="faq-categories"
              value={form.category}
              maxLength={100}
              placeholder="General"
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className={inputCls}
            />
            <datalist id="faq-categories">
              {categories.data?.map((c) => (
                <option key={c.category} value={c.category} />
              ))}
            </datalist>
          </div>
          <div>
            <label className={labelCls}>Order</label>
            <input
              type="number"
              step={1}
              value={form.order}
              onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>
        <label className="flex items-center gap-3 text-sm">
          <Switch
            checked={form.isActive}
            onCheckedChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
          />
          Active
        </label>
      </FormDialog>

      <FormDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this FAQ?"
        description={deleting?.question}
        onSubmit={runDelete}
        submitLabel="Delete"
        loading={remove.isPending}
        danger
      />
    </QueueLayout>
  );
}
